import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Loader2, AlertCircle, Calendar, Utensils, ClipboardList,
  StickyNote, TrendingDown, TrendingUp, Scale, Target, Camera,
  Activity, Ruler, ChevronDown, ChevronUp, History, ArrowRight,
  User, X, ZoomIn, ExternalLink
} from "lucide-react";
import { clientAuthAPI } from "../lib/api";
import { getInitials } from "../lib/avatarUtils";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from "recharts";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const resolvePhoto = (src) => {
  if (!src) return null;
  return src.startsWith("/api/") ? `${API_BASE}${src}` : src;
};

export default function ClientPortalPage({ shortLinkToken }) {
  const [searchParams] = useSearchParams();
  const token = shortLinkToken || searchParams.get("token");
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("journey");
  const [activeWeek, setActiveWeek] = useState(1);
  const [activePlanIdx, setActivePlanIdx] = useState(0);
  const [expandedPhoto, setExpandedPhoto] = useState(null);
  const [showProfilePhoto, setShowProfilePhoto] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!token || hasFetched.current) return;
    hasFetched.current = true;
    const load = async () => {
      try {
        const res = await clientAuthAPI.getDashboard(token);
        setData(res.data);
        setStatus("loaded");
      } catch {
        setStatus("error");
      }
    };
    load();
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#050505' }}>
        <Card className="glass-card max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2" data-testid="invalid-link-title">Invalid Link</h2>
            <p className="text-muted-foreground text-sm">This link is invalid. Please ask your coach for a new login link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <Loader2 className="w-8 h-8 text-primary animate-spin" data-testid="loading-spinner" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#050505' }}>
        <Card className="glass-card max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2" data-testid="error-title">Link Expired</h2>
            <p className="text-muted-foreground text-sm">This login link has expired. Please ask your coach for a new one.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { client, trainer_name, trainer_photo, active_plan, all_plans, progress, photos, notes, goal_history,
    trainer_bio, trainer_specialization, trainer_experience, trainer_instagram } = data;

  const clientPhoto = resolvePhoto(client?.profile_photo);
  const coachPhoto = resolvePhoto(trainer_photo);

  // Sort progress oldest first for charts
  const sortedProgress = progress && progress.length > 0
    ? [...progress].sort((a, b) => new Date(a.date) - new Date(b.date))
    : [];

  const latestProgress = sortedProgress.length > 0 ? sortedProgress[sortedProgress.length - 1] : null;
  const startWeight = client?.starting_weight;
  const currentWeight = latestProgress?.weight || client?.current_weight || startWeight;
  const weightChange = currentWeight && startWeight ? parseFloat((currentWeight - startWeight).toFixed(1)) : null;

  // Body fat data
  const progressWithBF = sortedProgress.filter(p => p.body_fat_percent);
  const latestBF = progressWithBF.length > 0 ? progressWithBF[progressWithBF.length - 1].body_fat_percent : null;
  const firstBF = progressWithBF.length > 0 ? progressWithBF[0].body_fat_percent : null;
  const bfChange = latestBF && firstBF ? parseFloat((latestBF - firstBF).toFixed(1)) : null;

  // Weeks elapsed
  const startDate = new Date(client?.start_date || client?.created_at);
  const latestDate = latestProgress ? new Date(latestProgress.date) : new Date();
  const weeksElapsed = Math.max(0, Math.floor((latestDate - startDate) / (1000 * 60 * 60 * 24 * 7)));

  // Chart data
  const weightChartData = (() => {
    const d = [];
    if (startWeight && client?.start_date) {
      const joinDate = new Date(client.start_date);
      const hasOnDate = sortedProgress.some(p => new Date(p.date).toDateString() === joinDate.toDateString());
      if (!hasOnDate) {
        d.push({ date: joinDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), weight: startWeight });
      }
    }
    sortedProgress.forEach(p => {
      d.push({
        date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weight: p.weight,
        bodyFat: p.body_fat_percent || null
      });
    });
    return d;
  })();

  const measurementChartData = sortedProgress
    .filter(p => p.chest || p.waist || p.hips || p.arms || p.thighs || p.calf || p.neck)
    .map(p => ({
      date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      chest: p.chest, waist: p.waist, hips: p.hips, arms: p.arms, thighs: p.thighs, calf: p.calf, neck: p.neck
    }));

  // Current plan for plans tab
  const plansList = all_plans || (active_plan ? [active_plan] : []);
  const selectedPlan = plansList[activePlanIdx] || null;
  const currentWeek = selectedPlan?.weeks?.find(w => w.week_number === activeWeek);

  const getGoalLabel = (goal) => {
    const labels = {
      fat_loss: "Fat Loss", muscle_gain: "Muscle Gain", maintenance: "Maintenance",
      recomp: "Recomposition", strength: "Strength", general_fitness: "General Fitness",
      competition_prep: "Competition Prep", endurance: "Endurance"
    };
    return labels[goal] || goal?.replace(/_/g, ' ') || "-";
  };

  const tabs = [
    { id: "journey", label: "Journey", icon: Activity },
    { id: "plans", label: "Plans", icon: ClipboardList },
    { id: "photos", label: "Photos", icon: Camera },
    { id: "notes", label: "Notes", icon: StickyNote },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: '#0B0B0F', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '10px 14px' }}>
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        {payload.map((p) => (
          <p key={p.name} className="text-sm font-bold" style={{ color: p.color }}>
            {p.name}: {p.value}{p.name === 'bodyFat' ? '%' : ' kg'}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-28" style={{ background: '#050505' }}>
      {/* Header */}
      <div className="glass border-b border-white/[0.06] sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            {/* Client Profile Photo */}
            <div
              className="w-14 h-14 rounded-full overflow-hidden cursor-pointer hover:opacity-80 transition-all flex-shrink-0"
              onClick={() => clientPhoto && setShowProfilePhoto(true)}
              data-testid="portal-client-photo"
            >
              {clientPhoto ? (
                <img src={clientPhoto} alt={client?.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; if (e.target.nextSibling) e.target.nextSibling.style.display='flex'; }} />
              ) : null}
              <div className={`w-full h-full bg-[#00BFFF]/10 items-center justify-center ${clientPhoto ? 'hidden' : 'flex'}`}>
                <span className="font-heading text-xl font-bold text-[#00BFFF]">
                  {getInitials(client?.name)}
                </span>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-base font-bold text-foreground uppercase tracking-wide truncate" data-testid="portal-client-name">
                {client?.name}
              </h1>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#00BFFF]/10 text-[#00BFFF] border border-[#00BFFF]/15" data-testid="portal-goal-badge">
                  {getGoalLabel(client?.goal)}
                </span>
                <span className="text-[10px] text-muted-foreground">&middot;</span>
                <span className="text-[10px] text-muted-foreground font-medium" data-testid="portal-weeks-badge">
                  {weeksElapsed} {weeksElapsed === 1 ? "week" : "weeks"}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="portal-coach-name">
                Coach: {trainer_name}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Photo Full Preview Modal */}
      {showProfilePhoto && clientPhoto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm" data-testid="profile-photo-modal">
          <button
            onClick={() => setShowProfilePhoto(false)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all"
            data-testid="profile-photo-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="relative max-w-[90vw] max-h-[80vh]">
            <img
              src={clientPhoto}
              alt={client?.name}
              className="max-w-full max-h-[80vh] object-contain rounded-2xl"
              data-testid="profile-photo-full"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent rounded-b-2xl p-4 text-center">
              <p className="text-sm font-bold text-white">{client?.name}</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Strip */}
      <div className="container mx-auto px-4 py-5">
        <div className="grid grid-cols-4 gap-2.5" data-testid="summary-strip">
          <div className="progress-glass p-3 text-center">
            <p className="text-[10px] text-muted-foreground mb-0.5 uppercase tracking-wider">Current</p>
            <p className="text-base font-bold text-foreground" data-testid="stat-current-weight">{currentWeight || '-'}<span className="text-[10px] text-muted-foreground ml-0.5">kg</span></p>
          </div>
          <div className="progress-glass p-3 text-center">
            <p className="text-[10px] text-muted-foreground mb-0.5 uppercase tracking-wider">Change</p>
            <p className={`text-base font-bold ${weightChange && weightChange < 0 ? 'text-green-400' : weightChange && weightChange > 0 ? 'text-red-400' : 'text-foreground'}`} data-testid="stat-weight-change">
              {weightChange != null ? `${weightChange > 0 ? '+' : ''}${weightChange}` : '-'}<span className="text-[10px] text-muted-foreground ml-0.5">kg</span>
            </p>
          </div>
          <div className="progress-glass p-3 text-center">
            <p className="text-[10px] text-muted-foreground mb-0.5 uppercase tracking-wider">Weeks</p>
            <p className="text-base font-bold text-[#00BFFF]" data-testid="stat-weeks">{weeksElapsed}</p>
          </div>
          <div className="progress-glass p-3 text-center">
            <p className="text-[10px] text-muted-foreground mb-0.5 uppercase tracking-wider">Goal</p>
            <p className="text-[11px] font-bold text-foreground leading-tight" data-testid="stat-goal">{getGoalLabel(client?.goal)}</p>
          </div>
        </div>
      </div>

      {/* Coach Bio Card */}
      {(trainer_bio || trainer_specialization || trainer_experience) && (
        <div className="container mx-auto px-4">
          <Card className="glass-card border-0 overflow-hidden" data-testid="coach-bio-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 bg-[#00BFFF]/10 flex items-center justify-center">
                  {coachPhoto ? (
                    <img src={coachPhoto} alt={trainer_name} className="w-12 h-12 rounded-full object-cover" onError={(e) => { e.target.style.display='none'; if (e.target.nextSibling) e.target.nextSibling.style.display='flex'; }} />
                  ) : null}
                  <div className={`w-12 h-12 rounded-full items-center justify-center ${coachPhoto ? 'hidden' : 'flex'}`}>
                    <span className="font-heading text-sm font-bold text-[#00BFFF]">{getInitials(trainer_name)}</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm font-bold text-foreground uppercase tracking-wide" data-testid="coach-bio-name">
                    {trainer_name}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                    {trainer_specialization && (
                      <span className="text-[11px] text-[#00BFFF] font-medium" data-testid="coach-bio-spec">{trainer_specialization}</span>
                    )}
                    {trainer_experience && (
                      <span className="text-[11px] text-muted-foreground" data-testid="coach-bio-exp">{trainer_experience}</span>
                    )}
                  </div>
                  {trainer_bio && (
                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed line-clamp-3" data-testid="coach-bio-text">{trainer_bio}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="container mx-auto px-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-smooth ${
                activeTab === tab.id
                  ? 'tab-active text-white'
                  : 'bg-white/[0.03] text-muted-foreground hover:text-foreground border border-white/[0.06]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 mt-5 space-y-5">

        {/* ======== JOURNEY TAB ======== */}
        {activeTab === "journey" && (
          <>
            {/* Weight History Graph */}
            {weightChartData.length > 1 && (
              <Card className="glass-card border-0" data-testid="weight-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-sm uppercase tracking-wide flex items-center gap-2">
                    <Scale className="w-4 h-4 text-[#00BFFF]" /> Weight History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={weightChartData}>
                        <defs>
                          <linearGradient id="weightGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00BFFF" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#00BFFF" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickLine={false} />
                        <YAxis stroke="#71717a" fontSize={10} tickLine={false} domain={['dataMin - 2', 'dataMax + 2']} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="weight" stroke="#00BFFF" strokeWidth={2.5} fill="url(#weightGrad)"
                          dot={{ fill: '#00BFFF', r: 3, strokeWidth: 0 }}
                          activeDot={{ r: 5, fill: '#00BFFF', stroke: '#fff', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Body Fat Graph */}
            {progressWithBF.length > 1 && (
              <Card className="glass-card border-0" data-testid="bodyfat-chart">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-sm uppercase tracking-wide flex items-center gap-2">
                      <Activity className="w-4 h-4 text-purple-400" /> Body Fat %
                    </CardTitle>
                    {bfChange != null && (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${bfChange < 0 ? 'bg-green-500/10 text-green-400' : bfChange > 0 ? 'bg-red-500/10 text-red-400' : 'bg-white/5 text-muted-foreground'}`}>
                        {bfChange > 0 ? '+' : ''}{bfChange}%
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={progressWithBF.map(p => ({
                        date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        bf: p.body_fat_percent
                      }))}>
                        <defs>
                          <linearGradient id="bfGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickLine={false} />
                        <YAxis stroke="#71717a" fontSize={10} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="bf" name="bodyFat" stroke="#a855f7" strokeWidth={2.5} fill="url(#bfGrad)"
                          dot={{ fill: '#a855f7', r: 3, strokeWidth: 0 }}
                          activeDot={{ r: 5, fill: '#a855f7', stroke: '#fff', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Measurements */}
            {measurementChartData.length > 0 && (
              <Card className="glass-card border-0" data-testid="measurements-section">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-sm uppercase tracking-wide flex items-center gap-2">
                    <Ruler className="w-4 h-4 text-amber-400" /> Measurements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {measurementChartData.length > 1 ? (
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={measurementChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                          <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickLine={false} />
                          <YAxis stroke="#71717a" fontSize={10} tickLine={false} />
                          <Tooltip content={<CustomTooltip />} />
                          {measurementChartData.some(d => d.chest) && <Line type="monotone" dataKey="chest" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} name="Chest" />}
                          {measurementChartData.some(d => d.waist) && <Line type="monotone" dataKey="waist" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} name="Waist" />}
                          {measurementChartData.some(d => d.hips) && <Line type="monotone" dataKey="hips" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 2 }} name="Hips" />}
                          {measurementChartData.some(d => d.arms) && <Line type="monotone" dataKey="arms" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} name="Arms" />}
                          {measurementChartData.some(d => d.thighs) && <Line type="monotone" dataKey="thighs" stroke="#06b6d4" strokeWidth={2} dot={{ r: 2 }} name="Thighs" />}
                          {measurementChartData.some(d => d.calf) && <Line type="monotone" dataKey="calf" stroke="#f97316" strokeWidth={2} dot={{ r: 2 }} name="Calf" />}
                          {measurementChartData.some(d => d.neck) && <Line type="monotone" dataKey="neck" stroke="#ec4899" strokeWidth={2} dot={{ r: 2 }} name="Neck" />}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <LatestMeasurements entry={measurementChartData[0]} />
                  )}
                  {/* Latest measurements summary below chart */}
                  {measurementChartData.length > 1 && (
                    <LatestMeasurements entry={measurementChartData[measurementChartData.length - 1]} />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Progress Timeline (Recent entries) */}
            {sortedProgress.length > 0 && (
              <Card className="glass-card border-0" data-testid="progress-timeline">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-sm uppercase tracking-wide flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-green-400" /> Recent Progress
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[...sortedProgress].reverse().slice(0, 5).map((entry, i) => (
                    <div key={entry.id || entry.date} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]" data-testid={`timeline-entry-${entry.id || entry.date}`}>
                      <div>
                        <p className="text-sm font-bold text-foreground">{entry.weight} kg</p>
                        <p className="text-[11px] text-muted-foreground">
                          {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {entry.body_fat_percent && <span className="ml-2 text-purple-400">BF: {entry.body_fat_percent}%</span>}
                        </p>
                      </div>
                      {i < [...sortedProgress].reverse().length - 1 && (() => {
                        const prev = [...sortedProgress].reverse()[i + 1];
                        const diff = parseFloat((entry.weight - prev.weight).toFixed(1));
                        return (
                          <span className={`text-xs font-bold flex items-center gap-1 ${diff < 0 ? 'text-green-400' : diff > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                            {diff < 0 ? <TrendingDown className="w-3 h-3" /> : diff > 0 ? <TrendingUp className="w-3 h-3" /> : null}
                            {Math.abs(diff)} kg
                          </span>
                        );
                      })()}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Goal History */}
            {goal_history && goal_history.length > 0 && (
              <Card className="glass-card border-0" data-testid="goal-history-section">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-sm uppercase tracking-wide flex items-center gap-2">
                    <Target className="w-4 h-4 text-[#00BFFF]" /> Goal History
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {goal_history.map((gh) => (
                    <div key={gh.changed_at} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04] text-xs" data-testid={`goal-history-${gh.changed_at}`}>
                      <span className="px-2 py-0.5 rounded-md bg-white/[0.05] text-muted-foreground font-medium">{getGoalLabel(gh.previous_goal)}</span>
                      <span className="text-muted-foreground">&#8594;</span>
                      <span className="px-2 py-0.5 rounded-md bg-[#00BFFF]/10 text-[#00BFFF] font-medium">{getGoalLabel(gh.new_goal)}</span>
                      <span className="text-muted-foreground ml-auto text-[11px]">{new Date(gh.changed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Empty state */}
            {sortedProgress.length === 0 && (
              <div className="empty-state p-8 text-center" data-testid="journey-empty">
                <Scale className="w-10 h-10 text-[#00BFFF]/20 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">Your fitness journey data will appear here as your coach logs your progress.</p>
              </div>
            )}
          </>
        )}

        {/* ======== PLANS TAB ======== */}
        {activeTab === "plans" && (
          <>
            {plansList.length === 0 ? (
              <div className="empty-state p-8 text-center" data-testid="plans-empty">
                <ClipboardList className="w-10 h-10 text-[#00BFFF]/20 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No workout plans yet. Your coach will add one soon.</p>
              </div>
            ) : (
              <>
                {/* Plan Selector */}
                {plansList.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-1" data-testid="plan-selector">
                    {plansList.map((p, idx) => (
                      <button
                        key={p.id || idx}
                        onClick={() => { setActivePlanIdx(idx); setActiveWeek(1); }}
                        className={`px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-smooth ${
                          activePlanIdx === idx
                            ? 'bg-[#00BFFF]/15 text-[#00BFFF] border border-[#00BFFF]/30'
                            : 'bg-white/[0.02] text-muted-foreground border border-white/[0.04] hover:border-[#00BFFF]/15'
                        }`}
                        data-testid={`plan-select-${idx}`}
                      >
                        {p.title} {p.is_active && <span className="text-[10px] ml-1 text-green-400">(Active)</span>}
                      </button>
                    ))}
                  </div>
                )}

                {selectedPlan && (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-foreground" data-testid="selected-plan-title">{selectedPlan.title}</h3>
                        <p className="text-[11px] text-muted-foreground">{selectedPlan.duration_weeks} weeks &middot; {new Date(selectedPlan.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                    </div>

                    {/* Week Selector */}
                    <div className="flex gap-2 overflow-x-auto">
                      {selectedPlan.weeks?.map(w => (
                        <button
                          key={w.week_number}
                          onClick={() => setActiveWeek(w.week_number)}
                          className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wide whitespace-nowrap transition-smooth ${
                            activeWeek === w.week_number
                              ? 'bg-[#00BFFF]/20 text-[#00BFFF] border border-[#00BFFF]/30'
                              : 'bg-white/[0.02] text-muted-foreground border border-white/[0.04] hover:border-[#00BFFF]/15'
                          }`}
                          data-testid={`week-${w.week_number}`}
                        >
                          Week {w.week_number}
                        </button>
                      ))}
                    </div>

                    {/* Workout Days */}
                    {currentWeek?.workout_days?.length > 0 ? (
                      currentWeek.workout_days.map((day, di) => (
                        <Card key={di} className="glass-card" data-testid={`workout-day-${di}`}>
                          <CardHeader className="pb-2">
                            <CardTitle className="font-heading text-base uppercase tracking-wide text-[#00BFFF]">
                              {day.day_name}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            {day.exercises?.map((ex, ei) => (
                              <div key={ei} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
                                <div>
                                  <p className="text-sm font-medium text-foreground">{ex.exercise_name}</p>
                                  {ex.notes && <p className="text-xs text-muted-foreground mt-0.5">{ex.notes}</p>}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                  <span className="font-bold text-foreground">{ex.sets}x{ex.reps}</span>
                                  {ex.weight && <span className="text-[#00BFFF]">{ex.weight}kg</span>}
                                </div>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="empty-state p-6 text-center">
                        <p className="text-muted-foreground text-sm">No workouts for Week {activeWeek}</p>
                      </div>
                    )}

                    {/* Diet for this week */}
                    {currentWeek?.diet_meals?.length > 0 && (
                      <>
                        <h4 className="font-heading text-xs uppercase tracking-widest text-muted-foreground mt-4 flex items-center gap-2">
                          <Utensils className="w-3 h-3" /> Diet - Week {activeWeek}
                        </h4>
                        {currentWeek.diet_meals.map((meal, mi) => (
                          <Card key={mi} className="glass-card" data-testid={`meal-${mi}`}>
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-bold text-[#00BFFF] uppercase tracking-wide">{meal.meal_name}</h3>
                                {meal.time && <span className="text-xs text-muted-foreground">{meal.time}</span>}
                              </div>
                              <p className="text-sm text-foreground">{meal.foods}</p>
                              {meal.notes && <p className="text-xs text-muted-foreground mt-2">{meal.notes}</p>}
                            </CardContent>
                          </Card>
                        ))}
                      </>
                    )}

                    {/* Week notes */}
                    {currentWeek?.notes && (
                      <div className="progress-glass p-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Coach Notes</p>
                        <p className="text-sm text-foreground">{currentWeek.notes}</p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* ======== PHOTOS TAB ======== */}
        {activeTab === "photos" && (
          <>
            {!photos?.length ? (
              <div className="empty-state p-8 text-center" data-testid="photos-empty">
                <Camera className="w-10 h-10 text-[#00BFFF]/20 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No progress photos yet.</p>
              </div>
            ) : (
              <>
                {/* Before & After Comparison */}
                {photos.length >= 2 && (
                  <BeforeAfterComparison photos={photos} />
                )}

                {/* All Photos Grid */}
                <div>
                  <h4 className="font-heading text-xs uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                    <Camera className="w-3 h-3" /> All Photos ({photos.length})
                  </h4>
                  <div className="grid grid-cols-2 gap-3" data-testid="photos-grid">
                    {photos.map((photo) => (
                      <div
                        key={photo.id || photo.date}
                        className="relative aspect-square rounded-xl overflow-hidden border border-white/[0.06] cursor-pointer hover:border-[#00BFFF]/30 transition-smooth"
                        onClick={() => setExpandedPhoto(expandedPhoto === (photo.id || photo.date) ? null : (photo.id || photo.date))}
                        data-testid={`photo-${photo.id || photo.date}`}
                      >
                        <img
                          src={resolvePhoto(photo.photo_data)}
                          alt={`Progress photo`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <p className="text-[10px] text-white/80">{new Date(photo.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                          <p className="text-[10px] text-[#00BFFF] uppercase font-bold">{photo.photo_type}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {/* Expanded photo overlay */}
            {expandedPhoto !== null && (() => { const photo = photos.find(p => (p.id || p.date) === expandedPhoto); return photo ? (
              <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setExpandedPhoto(null)} data-testid="photo-expanded">
                <img
                  src={resolvePhoto(photo.photo_data)}
                  alt="Progress"
                  className="max-w-full max-h-[80vh] object-contain rounded-xl"
                />
              </div>
            ) : null; })()}
          </>
        )}

        {/* ======== NOTES TAB ======== */}
        {activeTab === "notes" && (
          <>
            {!notes?.length ? (
              <div className="empty-state p-8 text-center" data-testid="notes-empty">
                <StickyNote className="w-10 h-10 text-[#00BFFF]/20 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No notes from your coach yet.</p>
              </div>
            ) : (
              notes.map((note) => (
                <Card key={note.id || note.created_at} className="glass-card" data-testid={`note-${note.id || note.created_at}`}>
                  <CardContent className="p-4">
                    <p className="text-sm text-foreground">{note.content}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-muted-foreground">{new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#00BFFF]/10 text-[#00BFFF]">{note.category}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="container mx-auto px-4 mt-12 pb-8 text-center">
        <div className="inline-flex items-center gap-2 mb-2">
          <img src="/logo192.png" alt="FitTrack Coach" className="w-5 h-5 rounded-md object-contain" />
          <span className="text-xs font-heading font-bold uppercase tracking-widest">
            <span className="text-white/60">FitTrack </span><span className="text-[#00E5FF]/80">COACH</span>
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground/50">
          <a
            href="https://app.fittrackcoach.in"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#00BFFF] transition-colors inline-flex items-center gap-1"
            data-testid="portal-footer-link"
          >
            app.fittrackcoach.in
            <ExternalLink className="w-3 h-3" />
          </a>
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">by InfiNest Tech Pvt. Ltd.</p>
      </div>
    </div>
  );
}

function LatestMeasurements({ entry }) {
  if (!entry) return null;
  const items = [
    { label: "Chest", val: entry.chest, color: "#f59e0b" },
    { label: "Waist", val: entry.waist, color: "#ef4444" },
    { label: "Hips", val: entry.hips, color: "#8b5cf6" },
    { label: "Arms", val: entry.arms, color: "#22c55e" },
    { label: "Thighs", val: entry.thighs, color: "#06b6d4" },
    { label: "Calf", val: entry.calf, color: "#f97316" },
    { label: "Neck", val: entry.neck, color: "#ec4899" },
  ].filter(i => i.val);
  if (!items.length) return null;
  return (
    <div className="grid grid-cols-4 gap-2 mt-3" data-testid="latest-measurements">
      {items.map(item => (
        <div key={item.label} className="text-center">
          <p className="text-base font-bold" style={{ color: item.color }}>{item.val}</p>
          <p className="text-[10px] text-muted-foreground">{item.label} (cm)</p>
        </div>
      ))}
    </div>
  );
}

function BeforeAfterComparison({ photos }) {
  const sorted = [...photos].sort((a, b) => new Date(a.date) - new Date(b.date));
  const before = sorted[0];
  const after = sorted[sorted.length - 1];
  const getSrc = (p) => resolvePhoto(p.photo_data);
  const daysBetween = Math.max(1, Math.round((new Date(after.date) - new Date(before.date)) / (1000 * 60 * 60 * 24)));

  return (
    <Card className="glass-card border-0 overflow-hidden" data-testid="before-after-comparison">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-sm uppercase tracking-wide flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#00BFFF]" /> Before & After
          </CardTitle>
          <span className="text-[11px] text-muted-foreground font-medium">{daysBetween} days apart</span>
        </div>
      </CardHeader>
      <CardContent className="p-3">
        <div className="grid grid-cols-2 gap-2" data-testid="before-after-grid">
          {/* Before */}
          <div className="relative rounded-xl overflow-hidden aspect-[3/4] border border-white/[0.06]" data-testid="before-photo">
            <img src={getSrc(before)} alt="Before" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
            <div className="absolute top-2 left-2">
              <span className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-white/15 backdrop-blur-md text-white border border-white/20">
                Before
              </span>
            </div>
            <div className="absolute bottom-2 left-2 right-2">
              <p className="text-[11px] text-white/90 font-medium">
                {new Date(before.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
          {/* After */}
          <div className="relative rounded-xl overflow-hidden aspect-[3/4] border border-[#00BFFF]/20" data-testid="after-photo">
            <img src={getSrc(after)} alt="After" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
            <div className="absolute top-2 left-2">
              <span className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-[#00BFFF]/25 backdrop-blur-md text-[#00BFFF] border border-[#00BFFF]/30">
                After
              </span>
            </div>
            <div className="absolute bottom-2 left-2 right-2">
              <p className="text-[11px] text-white/90 font-medium">
                {new Date(after.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
        {/* Transformation arrow strip */}
        <div className="flex items-center justify-center gap-3 mt-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.04]">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Transformation</span>
          <ArrowRight className="w-3.5 h-3.5 text-[#00BFFF]" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#00BFFF]">{daysBetween} days</span>
        </div>
      </CardContent>
    </Card>
  );
}
